import Clarity from '@microsoft/clarity';
import { useEffect, useState } from 'react';
import { useAuth } from 'src/auth';

// E2E: Skip Clarity initialization — no tracking needed during tests
if (process.env.CLARITY_PROJECT_ID && process.env.AGRIFUL_ENV !== 'e2e') {
  try {
    Clarity.init(process.env.CLARITY_PROJECT_ID);
  } catch {
    // Clarity failed to initialize — continue without tracking
  }
}

/** Framework-agnostic pathname hook — catches popstate + pushState/replaceState. */
function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const update = () => setPathname(window.location.pathname);

    window.addEventListener('popstate', update);

    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      origPush(...args);
      update();
    };
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      origReplace(...args);
      update();
    };

    return () => {
      window.removeEventListener('popstate', update);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  return pathname;
}

interface ClarityOrg {
  id?: string;
  name?: string;
}

/**
 * Identify the current user in MS Clarity with rich metadata.
 * Sets searchable custom tags for email, org, role, and internal IDs
 * so sessions can be found when debugging customer-reported issues.
 */
export function useIdentifyClarityUser(organization?: ClarityOrg | null) {
  const { currentUser } = useAuth();
  const pathname = usePathname();

  useEffect(() => {
    if (!currentUser || !window.clarity) return;

    try {
      const email =
        typeof currentUser.emailAddress === 'string'
          ? currentUser.emailAddress
          : currentUser.emailAddresses?.[0]?.emailAddress;
      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');

      // Grant consent — required for custom data processing (Oct 2025+)
      Clarity.consent();

      // Identify with Clerk user ID — called on every page for SPA route changes
      Clarity.identify(currentUser.id as string, '', '', fullName || '');

      // Custom tags for session filtering in the Clarity dashboard
      if (email) Clarity.setTag('email', email as string);
      if (fullName) Clarity.setTag('userName', fullName);
      if (organization?.name) Clarity.setTag('orgName', organization.name);
      if (organization?.id) Clarity.setTag('clerkOrgId', organization.id);
      if (currentUser.roles?.length) Clarity.setTag('roles', (currentUser.roles as string[]).join(','));
    } catch {
      // Clarity SDK error — non-critical, continue without tracking
    }
  }, [currentUser, organization?.id, pathname]);
}

// ---------------------------------------------------------------------------
// Clarity event & tag utilities
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

/** Emit a custom Clarity event (visible in the Clarity dashboard timeline). */
export function clarityEvent(name: string) {
  try {
    if (window.clarity) Clarity.event(name);
  } catch {
    /* noop */
  }
}

/** Set a custom tag on the current Clarity session. */
export function clarityTag(key: string, value: string) {
  try {
    if (window.clarity) Clarity.setTag(key, value);
  } catch {
    /* noop */
  }
}

/** Mark the current session as important (forces recording retention). */
export function clarityUpgrade(reason: string) {
  try {
    if (window.clarity) Clarity.upgrade(reason);
  } catch {
    /* noop */
  }
}
